import React from "react";
import { Box, Paper, Skeleton, Typography } from "@mui/material";
import NavigateToPageIcon from "@common/components/documentPreview/navigate-to-page-icon.svg?react";
import { DotLegalButton, DotLegalHeader, DotLegalInformationTooltip, DotLegalSelect } from "@dotlegal/dotlegal-ui-components";
import DotLegalCollapse from "@common/components/dotLegalCollapse/DotLegalCollapse";
import DotLegalStepHeader from "@common/components/dotLegalStepHeader/DotLegalStepHeader";
import { DotLegalSelectOption } from "@common/components/dotLegalMultiSelect/DotLegalMultiSelect.types";
import { useTranslation } from "@localization/useTranslation";
import { useLinkedEntityCollapseList } from "./LinkedEntityCollapseList.hooks";
import { useLinkedEntityCollapseListStyles } from "./LinkedEntityCollapseList.styles";

export interface LinkedEntityDetails {
    label: string;
    toolTip: string;
    getValues: (id: string) => Array<string> | undefined;
    isLoading: (id: string) => boolean;
}

export interface LinkedEntityCollapseListProps {
    header: string;
    selectedIds: Array<string>;
    selectableItems: Array<DotLegalSelectOption> | undefined;
    onChange: (ids: Array<string>) => void;
    getLinkTo: (id: string) => string | undefined;
    details?: LinkedEntityDetails;
    addButtonText: string;
    removeItemText: string;
    selectLabel: string;
    disabled: boolean;
    isLoading: boolean;
    isPreview: boolean;
    errorText?: string;
}

function LinkedEntityCollapseList(props: LinkedEntityCollapseListProps) {
    const { translateString } = useTranslation();
    const styles = useLinkedEntityCollapseListStyles();
    const {
        isDisabled,
        isLoading,
        isAdding,
        pickedId,
        cancelAdding,
        expandedIndex,
        getName,
        getLink,
        getSelectableItems,
        onExpandClick,
        startAdding,
        addItem,
        swapItem,
        removeItem,
    } = useLinkedEntityCollapseList(props);

    function getLinkIcon(id: string) {
        const link = getLink(id);
        return (
            link && {
                icon: <NavigateToPageIcon height={20} width={20} strokeWidth={1.8} />,
                iconHoverText: link.hoverText,
                href: link.href,
            }
        );
    }

    function getSelector(onSelect: (id: string) => void, currentId?: string) {
        return (
            <Box sx={styles.selector}>
                <DotLegalSelect
                    isLoading={isLoading}
                    options={getSelectableItems(currentId)}
                    selectedItem={currentId}
                    onChange={(id) => onSelect(id!)}
                    placeholder={props.selectLabel}
                    label={props.selectLabel}
                    noOptionsLabel={translateString("noOptions")}
                    disabled={isDisabled}
                    disableClearable
                />
            </Box>
        );
    }

    function getDetailsBox(id: string) {
        // An item the user cannot open has no values and shows an empty value, so its card is as tall as any other.
        return (
            props.details && (
                <Paper elevation={0} sx={styles.detailsBox}>
                    <Box sx={styles.detailsRow}>
                        <DotLegalInformationTooltip text={props.details.toolTip} />
                        <DotLegalHeader headerStyle="small" marginBottom={0}>
                            {props.details.label}:
                        </DotLegalHeader>
                        {props.details.isLoading(id) ? (
                            <Skeleton sx={styles.detailsValueSkeleton} />
                        ) : (
                            <DotLegalHeader headerStyle="small" marginBottom={0} fontWeight={400}>
                                {(props.details.getValues(id) ?? []).join(", ")}
                            </DotLegalHeader>
                        )}
                    </Box>
                </Paper>
            )
        );
    }

    function getCard(id: string, index: number) {
        if (isLoading) {
            return <Skeleton key={index} height={80} width="100%" />;
        }

        return (
            <DotLegalCollapse
                key={index}
                sx={styles.collapse}
                disabled={isDisabled}
                centerContent={<Typography sx={styles.collapseHeaderText}>{getName(id)}</Typography>}
                isExpanded={index === expandedIndex}
                onExpandClick={() => onExpandClick(index)}
                onRemoveClick={() => removeItem(index)}
                removeItemText={props.removeItemText}
                removeItemStyle="link"
                collapseIcon={getLinkIcon(id)}
            >
                {getSelector((newId) => swapItem(index, newId), id)}
                {getDetailsBox(id)}
            </DotLegalCollapse>
        );
    }

    // Keyed by the position the picked item will take, so the saved item's card takes over this card in place.
    function getAddingCard() {
        return (
            <DotLegalCollapse
                key={props.selectedIds.length}
                sx={styles.collapse}
                isExpanded
                onExpandClick={() => {}}
                onRemoveClick={cancelAdding}
                removeItemText={props.removeItemText}
                removeItemStyle="link"
            >
                {getSelector(addItem, pickedId)}
            </DotLegalCollapse>
        );
    }

    return (
        <>
            <DotLegalStepHeader bottomMargin>{props.header}</DotLegalStepHeader>
            {[...props.selectedIds.map(getCard), ...(isAdding ? [getAddingCard()] : [])]}

            <Box sx={styles.addButton}>
                <DotLegalButton buttonType="secondary" disabled={isAdding || isDisabled} onClick={startAdding}>
                    {props.addButtonText}
                </DotLegalButton>
            </Box>

            {props.errorText && <Box sx={styles.errorText}>{props.errorText}</Box>}
        </>
    );
}

export default LinkedEntityCollapseList;
